import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Map,
  APIProvider,
  Marker,
  useMap,
  type MapMouseEvent,
} from "@vis.gl/react-google-maps";
import { useSearchParams } from "react-router-dom";
import DashboardPage from "../../layout";
import Button from "../../components/Button";
import {
  fetchDeliveryLocationForm,
  submitDeliveryLocationForm,
} from "../../api/deliveryLocationFormOpen";
import { toast, ToastContainer } from "react-toastify";

/** Same as delivery heatmap (`saudiCenter`). */
const saudiCenter = { lat: 23.8859, lng: 45.0792 };
const USER_ZOOM = 16;

type LatLng = { lat: number; lng: number };

const circleIcon = (fillColor: string, scale = 9): google.maps.Symbol => ({
  path: 0,
  fillColor,
  fillOpacity: 1,
  strokeColor: "#ffffff",
  strokeWeight: 2,
  scale,
});

/** When GPS and pin share the same coords, markers stack; nudge the user dot slightly so both show. */
function getUserMarkerPosition(user: LatLng, pinPos: LatLng): LatLng {
  const dLat = Math.abs(user.lat - pinPos.lat);
  const dLng = Math.abs(user.lng - pinPos.lng);
  const overlap = dLat < 1e-5 && dLng < 1e-5;
  if (overlap) {
    return { lat: user.lat + 0.00022, lng: user.lng };
  }
  return user;
}

const PanToUserOnce: React.FC<{ target: LatLng | null }> = ({ target }) => {
  const map = useMap();
  const didPan = useRef(false);

  useEffect(() => {
    if (!map || !target || didPan.current) return;
    didPan.current = true;
    map.panTo(target);
    map.setZoom(USER_ZOOM);
  }, [map, target]);

  return null;
};

const DeliveryLocationForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";

  const [initLoading, setInitLoading] = useState(true);
  const [deliveryNumber, setDeliveryNumber] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [userLocation, setUserLocation] = useState<LatLng | null>(null);
  /** `prompt` = show in-app permission UI; then browser dialog when user taps Allow. */
  const [geoStatus, setGeoStatus] = useState<
    "prompt" | "pending" | "ok" | "unavailable" | "skipped"
  >("prompt");

  const [pin, setPin] = useState<LatLng>(saudiCenter);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoadError("Invalid link: missing token.");
      setInitLoading(false);
      return;
    }
    (async () => {
      try {
        const { delivery_number } = await fetchDeliveryLocationForm(token);
        if (!cancelled) setDeliveryNumber(delivery_number);
      } catch (e: unknown) {
        const msg =
          e && typeof e === "object" && "message" in e && typeof (e as Error).message === "string"
            ? (e as Error).message
            : "Could not load delivery details. The link may be invalid or expired.";
        if (!cancelled) setLoadError(msg);
      } finally {
        if (!cancelled) setInitLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoStatus("unavailable");
    }
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoStatus("unavailable");
      return;
    }
    setGeoStatus("pending");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const loc = { lat, lng };
        setUserLocation(loc);
        setPin(loc);
        setGeoStatus("ok");
      },
      () => setGeoStatus("unavailable"),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }, []);

  const skipLocation = useCallback(() => {
    setGeoStatus("skipped");
  }, []);

  const userMarkerPosition = useMemo(
    () => (userLocation ? getUserMarkerPosition(userLocation, pin) : null),
    [userLocation, pin]
  );

  const onPinDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
    const latLng = e.latLng;
    if (!latLng) return;
    setPin({ lat: latLng.lat(), lng: latLng.lng() });
  }, []);

  const onMapClick = useCallback((e: MapMouseEvent) => {
    const latLng = e.detail.latLng;
    if (!latLng) return;
    setPin({ lat: latLng.lat, lng: latLng.lng });
  }, []);

  const handleSubmit = async () => {
    if (!token || !deliveryNumber) return;
    setSubmitting(true);
    try {
      await submitDeliveryLocationForm({
        delivery_number: token,
        latitude: String(pin.lat),
        longitude: String(pin.lng),
      });
      toast.success("Location submitted successfully.");
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e && typeof (e as Error).message === "string"
          ? (e as Error).message
          : "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardPage>
      <ToastContainer position="top-center" />
      {/* Same outer shell as delivery heatmap — `h-full` only works inside DashboardPage’s fixed-height scroll area */}
      <div className="flex flex-col h-full w-full p-4 gap-4">
        <div className="flex flex-col gap-2 shrink-0">
          <h1 className="text-xl font-semibold text-gray-800">Confirm delivery location</h1>
          <p className="text-sm text-gray-600">
            Move the pin or tap the map to set where the delivery should go, then submit.
          </p>
          {deliveryNumber && (
            <p className="text-sm text-gray-500">
              Delivery: <span className="font-medium text-gray-800">{deliveryNumber}</span>
            </p>
          )}
          {initLoading && (
            <p className="text-xs text-gray-600 flex items-center gap-2">
              <span className="inline-block h-3 w-3 border-2 border-purple border-t-transparent rounded-full animate-spin shrink-0" />
              Loading delivery information…
            </p>
          )}
          {loadError && !initLoading && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {loadError}
            </p>
          )}
          {geoStatus === "skipped" && (
            <div className="text-xs text-gray-600 flex flex-wrap items-center gap-2">
              <span>Location not shared. Drag the pin on the map to set your address.</span>
              {navigator.geolocation && (
                <button
                  type="button"
                  className="text-purple font-medium underline underline-offset-2 shrink-0"
                  onClick={() => setGeoStatus("prompt")}
                >
                  Share location
                </button>
              )}
            </div>
          )}
          {geoStatus === "unavailable" && (
            <div className="text-xs text-amber-700 flex flex-wrap items-center gap-2">
              <span>
                Location could not be used (blocked or unavailable). Drag the pin on the map to set
                your address, or enable location in your browser settings.
              </span>
              {navigator.geolocation && (
                <button
                  type="button"
                  className="text-purple font-medium underline underline-offset-2 shrink-0"
                  onClick={() => setGeoStatus("prompt")}
                >
                  Try again
                </button>
              )}
            </div>
          )}
        </div>

        {/* Identical map block to `delivery_heatmap/index.tsx` (lines 541–588) */}
        <div
          className="flex-1 relative rounded-lg overflow-hidden border border-gray-200 shadow-sm"
          style={{ minHeight: "500px" }}
        >
          {(geoStatus === "prompt" || geoStatus === "pending") && navigator.geolocation && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/45 p-4">
              <div
                className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 border border-gray-200"
                role="dialog"
                aria-modal="true"
                aria-labelledby="location-prompt-title"
              >
                <h2
                  id="location-prompt-title"
                  className="text-lg font-semibold text-gray-900"
                >
                  Allow location access
                </h2>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                  To show your position on the map and center it on you, we need permission to access
                  your location. Your browser will ask you to allow or block—choose Allow to continue.
                </p>
                {geoStatus === "pending" ? (
                  <div className="mt-6 flex items-center gap-3 text-sm text-gray-700">
                    <span className="inline-block h-5 w-5 border-2 border-purple border-t-transparent rounded-full animate-spin shrink-0" />
                    <span>Waiting for your choice in the browser prompt…</span>
                  </div>
                ) : (
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Button type="purple" label="Allow location" onClick={requestLocation} />
                    <Button type="grey" label="Not now" onClick={skipLocation} />
                  </div>
                )}
              </div>
            </div>
          )}
          <APIProvider
            apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY || ""}
            libraries={["visualization"]}
          >
            <Map
              style={{ width: "100%", height: "100%" }}
              defaultZoom={7}
              gestureHandling="greedy"
              disableDefaultUI={true}
              zoomControl={true}
              minZoom={6}
              maxZoom={18}
              onClick={onMapClick}
              restriction={{
                latLngBounds: {
                  north: 32.5,
                  south: 16.0,
                  east: 56.0,
                  west: 34.0,
                },
                strictBounds: true,
              }}
            >
              {userLocation && <PanToUserOnce target={userLocation} />}
              {userMarkerPosition && (
                <Marker
                  position={userMarkerPosition}
                  icon={circleIcon("#4285F4", 12)}
                  title="Your current location (GPS)"
                  zIndex={3}
                />
              )}
              <Marker
                position={pin}
                draggable
                onDragEnd={onPinDragEnd}
                icon={circleIcon("#b91c1c", 11)}
                title="Delivery pin — drag to adjust"
                zIndex={2}
              />
            </Map>
          </APIProvider>
        </div>

        <div className="shrink-0">
          <Button
            type="purple"
            label={submitting ? "Submitting…" : "Submit location"}
            onClick={handleSubmit}
            disabled={!deliveryNumber || submitting}
          />
        </div>
      </div>
    </DashboardPage>
  );
};

export default DeliveryLocationForm;
